import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import { FiCamera } from "react-icons/fi";
import { createUserWithEmailAndPassword, signInWithCredential } from "firebase/auth";
import { auth, database, storage } from "../constants/firebase";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { get, ref, set } from "firebase/database";
import { createSession, resizeImage, saveToken } from "../utils/utils";
import ImageCropModal from "../components/ImageCropModal";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { getNames } from 'country-list';
import Select from 'react-select';


// Country select styling to match your Niche select
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '0.75rem', // rounded-xl
        padding: '0.75rem 1rem', // px-4 py-3
        backgroundColor: '#F5F9FE', // same as bg
        borderColor: state.isFocused ? '#E5E7EB' : '#E5E7EB', // gray-200 border
        boxShadow: 'none',
        minHeight: '48px',
        cursor: 'pointer',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: '#000',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#7C8BA0', // placeholder color
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0 0rem',
        color: '#000',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#F5F9FE', // same background as select
        borderRadius: '0.75rem',
        marginTop: '4px',
        border: '1px solid #E5E7EB', // same border color
        zIndex: 50,
        overflow: 'hidden',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#E5E7EB' : '#F5F9FE', // hover color same as input border hover
        color: '#000',
        cursor: 'pointer',
        padding: '0.75rem 1rem',
    }),
};


const SignUp = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const [formData, setFormData] = useState({
        uid: '',
        name: '',
        email: '',
        password: '',
        username: '',
        about: '',
        niche: 'Filmmaker',
        location: '',
        country: '',
        profilePic: '',
        agreeToTerms: false,
        isAdmin: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [googleSignUp, setGoogleSignUp] = useState(false);
    const [errors, setErrors] = useState({});
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState("");
    const [tempImage, setTempImage] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const profilePicInputRef = useRef(null);
    const { refreshUser } = useUser();
    const [googleProvidedName, setGoogleProvidedName] = useState(false);
    const [googleProvidedEmail, setGoogleProvidedEmail] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;

    useEffect(() => {
        const countries = getNames().map(name => ({ value: name, label: name }));
        setCountryOptions(countries);
    }, []);

    // Initialize with location state
    useEffect(() => {
        if (location.state?.isGoogleSignUp) {
            setGoogleSignUp(true);
            setFormData(prev => ({
                ...prev,
                uid: location.state.uid || "",
                name: location.state.name || "",
                email: location.state.email || "",
                profilePic: location.state.profilePic || ""
            }));
            if (location.state.profilePic) {
                setProfilePicPreview(location.state.profilePic);
            }
            setGoogleProvidedName(!!location.state.name);
            setGoogleProvidedEmail(!!location.state.email);
        }
    }, [location.state]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleProfilePicChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const imageUrl = URL.createObjectURL(file);
        setTempImage(imageUrl);
        setShowCropModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!googleSignUp) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (!passwordRegex.test(formData.password)) {
                newErrors.password = 'Password must be 8+ chars, include letters, numbers & symbols';
            }
        }
        if (!formData.username) newErrors.username = 'Username is required';
        if (!formData.niche) newErrors.niche = 'Niche is required';
        if (!formData.location) newErrors.location = 'Location is required';
        if (!formData.country) newErrors.country = 'Country is required';
        // if (!formData.about) newErrors.about = 'About is required';
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
        if (!profilePicFile && !formData.profilePic) newErrors.profilePic = 'Profile picture is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setIsSubmitting(false);
            return;
        }

        try {
            if (googleSignUp) {
                let profilePicUrl = formData.profilePic || "";
                if (profilePicFile) {
                    const picRef = storageRef(storage, `profilePictures/${formData.uid}`);
                    await uploadBytes(picRef, profilePicFile);
                    profilePicUrl = await getDownloadURL(picRef);
                }
                await set(ref(database, 'Users/' + formData.uid), {
                    uid: formData.uid,
                    name: formData.name,
                    username: formData.username,
                    about: formData.about,
                    email: formData.email,
                    niche: formData.niche,
                    location: formData.location,
                    country: formData.country,
                    role: 'Free',
                    provider: "google",
                    isTeamUser: false,
                    createdAt: new Date().toISOString(),
                    isAdmin: false,
                    profilePic: profilePicUrl,
                });
                localStorage.setItem("uid", formData.uid);
                await refreshUser();
                // ✅ create new sessionId (kicks old session)
                await createSession(formData);
                // ✅ save FCM token only for this session
                await saveToken(formData);
                // 👇 Send message to React Native app if inside WebView
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "USER_LOGGED_IN",
                            uid: formData.uid,
                            email: formData.email,
                        })
                    );
                }
                navigate(ROUTES.DASHBOARD, { replace: true });
            } else {
                const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
                const user = userCredential.user;

                const picRef = storageRef(storage, `profilePictures/${user.uid}`);
                await uploadBytes(picRef, profilePicFile);
                const profilePicUrl = await getDownloadURL(picRef);

                await set(ref(database, 'Users/' + user.uid), {
                    uid: user.uid,
                    name: formData.name,
                    username: formData.username,
                    about: formData.about,
                    email: user.email,
                    password: formData.password,
                    niche: formData.niche,
                    location: formData.location,
                    country: formData.country,
                    role: 'Free',
                    isTeamUser: false,
                    createdAt: new Date().toISOString(),
                    isAdmin: false,
                    profilePic: profilePicUrl,
                });

                localStorage.setItem("uid", user.uid);
                await refreshUser();
                // ✅ create new sessionId (kicks old session)
                await createSession(user);
                // ✅ save FCM token only for this session
                await saveToken(user);
                // 👇 Send message to React Native app if inside WebView
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "USER_LOGGED_IN",
                            uid: user.uid,
                            email: user.email,
                        })
                    );
                }
                navigate(ROUTES.DASHBOARD, { replace: true });
            }
        } catch (error) {
            console.error("Firebase sign up error:", error.message);
            setErrors({ firebase: error.message });
        } finally {
            setIsSubmitting(false);
        }

    };


    const processFirebaseUser = useCallback(async (user) => {
        const userRef = ref(database, "Users/" + user.uid);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
            navigate(ROUTES.SIGN_UP, {
                state: {
                    isGoogleSignUp: true,
                    uid: user.uid,
                    name: user.displayName || "",
                    email: user.email || "",
                    profilePic: user.photoURL || "",
                },
            });
        } else {
            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);
            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            navigate(ROUTES.DASHBOARD, { replace: true });
        }
    }, [navigate, refreshUser]);

    const handleGoogleSignUp = async () => {
        if (window.isInApp) {
            // Inside React Native WebView → request native login
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "GOOGLE_LOGIN" })
            );
        } else {
            // Normal Web login
            const provider = new GoogleAuthProvider();
            try {
                const result = await signInWithPopup(auth, provider);
                await processFirebaseUser(result.user);
            } catch (err) {
                console.error("Google login failed:", err.message);
            }
        }
    };

    // Listen for native Google login result from WebView
    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const message = JSON.parse(event.data);

                if (message.type === "GOOGLE_LOGIN_SUCCESS") {
                    const credential = GoogleAuthProvider.credential(message.token);
                    const result = await signInWithCredential(auth, credential);
                    await processFirebaseUser(result.user);
                }

                if (message.type === "GOOGLE_LOGIN_ERROR") {
                    console.error("Native Google login failed:", message.error);
                }
            } catch { }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, [processFirebaseUser]);


    return (
        <div className="bg-main w-full flex items-center justify-center px-4 sm:px-6 md:px-8 py-12">
            <div className="w-full max-w-md mt-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-4xl font-bold dark:text-white text-black mb-2">
                        Sign Up
                    </h3>
                    <p className="text-sm md:text-base dark:text-gray-300 text-gray-700">
                        Create your account by filling in the details below.
                    </p>
                    {googleSignUp && (
                        <div className="max-w-md mx-auto mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500">
                            <p className="text-blue-300 text-sm">
                                <strong>Almost there!</strong>  You'll need to complete this information to use all features
                            </p>
                        </div>
                    )}
                    {errors.firebase && <p className="text-red-500 text-sm mt-3">{errors.firebase}</p>}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Profile Picture Upload */}
                    <div className="flex flex-col items-center gap-2">
                        <button
                            type="button"
                            onClick={() => profilePicInputRef.current?.click()}
                            className={`relative w-24 h-24 rounded-full overflow-hidden border-2 ${errors.profilePic ? 'border-red-500' : 'border-[#327691]'
                                } flex items-center justify-center bg-[#F5F9FE] dark:bg-white`}
                        >
                            {profilePicPreview ? (
                                <img
                                    src={profilePicPreview}
                                    alt="Profile preview"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <FiCamera className="text-3xl text-gray-400" />
                            )}
                            <div className="absolute inset-0 bg-black/20 flex items-end justify-center pb-1">
                                <FiCamera className="text-white text-sm" />
                            </div>
                        </button>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Profile photo <span className="text-red-500">*</span></span>
                        {errors.profilePic && <p className="text-red-400 text-xs">{errors.profilePic}</p>}
                        <input
                            ref={profilePicInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleProfilePicChange}
                        />
                    </div>

                    {/* Name Input */}
                    <div>
                        <label htmlFor="name" className="sr-only">Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            disabled={googleSignUp && googleProvidedName}
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Name"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.name ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email Input */}
                    <div>
                        <label htmlFor="email" className="sr-only">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            disabled={googleSignUp && googleProvidedEmail}
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.email ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Password Input */}
                    {
                        !googleSignUp &&
                        <div className="relative">
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Password"
                                className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                required
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <FaRegEye size={18} className="text-black" />
                                ) : (
                                    <FaRegEyeSlash size={18} className="text-black" />
                                )}
                            </button>
                            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                        </div>
                    }


                    {/* Username Input */}
                    <div>
                        <label htmlFor="username" className="sr-only">Username</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            placeholder="Username"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.username ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username}</p>}
                    </div>

                    <div>
                        <label htmlFor="username" className="sr-only">About</label>
                        {/* About Textarea */}
                        <textarea
                            name="about"
                            placeholder="About"
                            value={formData.about}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />
                        {errors.about && <p className="text-red-400 text-xs mt-1">{errors.about}</p>}
                    </div>

                    {/* Niche Selector */}
                    <div>
                        <label htmlFor="niche" className="sr-only">Niche</label>
                        <select
                            id="niche"
                            name="niche"
                            value={formData.niche}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option disabled selected>Select your niche</option>
                            <option value="Filmmaker">Filmmaker</option>
                            <option value="Content Creator">Content Creator</option>
                            <option value="Drone Operator">Drone Operator</option>
                            <option value="Gaffer">Gaffer</option>
                            <option value="Sound Designer">Sound Designer</option>
                            <option value="VFX Artist">VFX Artist</option>
                            <option value="Editor">Editor</option>
                        </select>
                    </div>
                    {errors.niche && <p className="text-red-400 text-xs mt-1">{errors.niche}</p>}

                    <div>
                        <label htmlFor="country" className="sr-only">Country</label>
                        <Select
                            id="country"
                            name="country"
                            options={countryOptions}
                            value={countryOptions.find(option => option.value === formData.country)}
                            onChange={(selectedOption) =>
                                handleChange({ target: { name: 'country', value: selectedOption.value } })
                            }
                            placeholder="Select your country"
                            isSearchable
                            styles={customSelectStyles}
                        />
                        {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                    </div>


                    {/* Location Input */}
                    <div>
                        <label htmlFor="location" className="sr-only">Location</label>
                        <input
                            type="text"
                            id="location"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Location"
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />
                    </div>
                    {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}

                    {/* Terms Checkbox */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="agreeToTerms"
                            name="agreeToTerms"
                            checked={formData.agreeToTerms}
                            onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="agreeToTerms" className="ml-2 text-sm dark:text-gray-300 text-gray-700">
                            I agree to the <a href="https://rentry.co/terms-of-service-zylen" target="_blank" className="text-primary" rel="noreferrer">Terms of Service</a> and <a href="https://www.termsfeed.com/live/bf8eefa4-d69e-4ed8-8e42-ae42fedc5081" target="_blank" className="text-primary" rel="noreferrer">Privacy Policy</a>
                        </label>
                    </div>
                    {errors.agreeToTerms && <p className="text-red-400 text-xs">{errors.agreeToTerms}</p>}
                    {/* Login Button */}
                    <button
                        type="submit"
                        className="btn-primary w-full"
                    >
                        {isSubmitting ? "Creating..." : "Create Account"}
                    </button>
                </form>

                {/* Social Login */}
                <div className="mt-6 flex flex-col gap-3">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-main text-gray-400">
                                Or
                            </span>
                        </div>
                    </div>


                    <div className="mt-3">

                        {/* Google Login */}
                        <button
                            type="button"
                            className="btn-secondary w-full flex items-center justify-center"
                            onClick={handleGoogleSignUp}
                        >
                            <span className="sr-only">Sign Up with Google</span>
                            <img src={ICONS.google} alt="Google" className="h-5 w-5" />
                            <span className="ml-2 text-light-purple">Sign Up with Google</span>
                        </button>

                    </div>
                </div>

                {/* Sign Up Link */}
                <div className="mt-3 text-center">
                    <p className="dark:text-white text-[16px] text-gray-700">
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate(ROUTES.SIGN_IN)}
                            className="text-primary"
                        >
                            Sign In
                        </button>
                    </p>
                </div>
            </div>

            {showCropModal && tempImage && (
                <ImageCropModal
                    image={tempImage}
                    aspect={1}
                    onCancel={() => { setShowCropModal(false); setTempImage(null); }}
                    onCropComplete={async (croppedBlob) => {
                        try {
                            const resizedBlob = await resizeImage(croppedBlob, 200);
                            setProfilePicFile(resizedBlob);
                            setProfilePicPreview(URL.createObjectURL(resizedBlob));
                            setErrors(prev => ({ ...prev, profilePic: undefined }));
                        } catch {
                            setProfilePicFile(croppedBlob);
                            setProfilePicPreview(URL.createObjectURL(croppedBlob));
                        }
                        setShowCropModal(false);
                        setTempImage(null);
                    }}
                />
            )}
        </div>
    );
};

export default SignUp;